<template>
  <div class="wrapper">
    <div class="main visual-2 doc-appoint-list-main">
      <Header />

      <div v-if="loading" class="loader">
        <!-- You can replace this with a custom loader or spinner -->
        <div class="spinner"></div>
      </div>

      <div v-else class="doc-appointment-data">
        <!-- Doctor Info, Working Days, and Time Slots -->
        <div class="doctor-overview">
          <div class="box doctor-profile">
            <router-link :to="`/doctor-profile?id=${this.$route.query.id}`">
              <button class="edit-btn">Edit</button>
            </router-link>

            <img class="doctor-image" :src="doctor?.photo || require('@/assets/img/doctor-james.png')"
              alt="Doctor Profile" />
            <div class="doctor-info">
              <h3 class="doctor-name">
                {{ doctor?.firstname + " " + doctor?.lastname }}
              </h3>
              <p class="doctor-address">
                <i class="fa-solid fa-stethoscope"></i>
                {{ doctor?.speciality || "N/A" }}
              </p>
              <p class="doctor-address">
                <i class="fa-solid fa-language"></i>
                {{ doctor?.languagesSpoken?.join(", ") || "N/A" }}
              </p>

              <p class="doctor-address">
                <i class="fa-solid fa-briefcase"></i>
                {{ doctor?.experienceYears }} years experience
              </p>

              <p class="doctor-address">
                <i class="fa-solid fa-venus-mars"></i>
                {{ doctor?.gender || "N/A" }}
              </p>

              <p class="doctor-address">
                <i class="fa-solid fa-location-dot"></i>
                <span>{{
                  doctor?.hospital?.address || "No address provided"
                }}</span>
              </p>
            </div>
          </div>
          <div class="box doctor-schedule">
            <div class="schedule-section">
              <h4>Working Days</h4>
              <div class="schedule-days">
                <span v-for="(day, index) in uniqueDays" :key="index" class="work-day"
                  :class="{ selected: selectedDay === day }" @click="selectedDay = day">
                  {{ day }}
                </span>
              </div>
            </div>
          </div>
          <div class="box doctor-schedule">
            <div class="schedule-section">
              <h4>Available Time Slots</h4>

              <!-- Appointment Type Dropdown -->
              <div class="appointment-type-filter mb-3">
                <label for="apptType">Select Appointment Type:</label>
                <select id="apptType" v-model="selectedAppointmentType" class="form-select">
                  <option value="">Select Type</option>
                  <option v-for="(type, index) in appointmentTypes" :key="index" :value="type">
                    {{ type.description || type.label }}
                  </option>
                </select>
              </div>

              <!-- Slots Section -->
              <div class="time-slots">
                <div v-if="slotsLoading" class="slots-loader">
                  <div class="mini-spinner"></div>
                  <p>Loading time slots...</p>
                </div>

                <div v-else-if="!selectedAppointmentType && selectedDay" class="no-slot-message text-muted">
                  Please select an appointment type to view slots.
                </div>

                <span v-else-if="
                  filteredSlots.length === 0 && selectedAppointmentType
                " class="text-danger">
                  No slots available for this type.
                </span>

                <span v-for="(slot, index) in filteredSlots" :key="index" class="time-slot">
                  {{ formatTime(slot.startTime) }} to
                  {{ formatTime(slot.endTime) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Appointment Lists (Unchanged) -->
        <div class="appointments-list">
          <div class="tables-row">
            <!-- Today Appointments -->
            <div class="box appointments-section">
              <h4>Today Appointment</h4>
              <div class="appointments-table-wrapper">
                <table class="appointments-table">
                  <thead>
                    <tr>
                      <th>Patient Name</th>
                      <th>Appointment ID</th>
                      <th>Date</th>
                      <th>Time</th>
                      <!-- <th>View</th> -->
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="appointment in todayAppointments" :key="appointment.id">
                      <td>{{ $formatName(appointment.patient) }}</td>
                      <td>{{ appointment.appointmentCode }}</td>
                      <td> {{ formatDate(appointment?.assignDate || appointment?.assignedStartTime) }}</td>
                      <td style="padding-left: 10px;">
                        {{ formatTime(appointment.assignedStartTime) }} to
                        {{ formatTime(appointment.assignedEndTime) }}
                      </td>
                      <!-- <td><a href="#" class="view-link">View</a></td> -->
                      <td>
                        <p :class="statusClass(appointment?.status)">
                          {{ appointment.status }}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Upcoming Appointments -->
            <div class="box appointments-section">
              <h4>Upcoming Appointment</h4>
              <div class="appointments-table-wrapper">
                <table class="appointments-table">
                  <thead>
                    <tr>
                      <th>Patient Name</th>
                      <th>Appointment ID</th>
                      <th>Date</th>

                      <th>Time</th>
                      <!-- <th>View</th> -->
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="appointment in upcomingAppointments" :key="appointment.id">
                      <td>{{ $formatName(appointment.patient) }}</td>
                      <td>{{ appointment.appointmentCode }}</td>
                      <td>{{ formatDate(appointment?.assignDate || appointment?.assignedStartTime) }}</td>
                      <td style="padding-left: 10px;">
                        {{ formatTime(appointment.assignedStartTime) }} to
                        {{ formatTime(appointment.assignedEndTime) }}
                      </td>
                      <!-- <td><a href="#" class="view-link">View</a></td> -->
                      <td>
                        <p :class="statusClass(appointment?.status)">
                          {{ appointment.status }}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterView />
    </div>
  </div>
</template>
<script>
// const appointmentTypeNames = {
//   NPSHORT: "New Patient Short Exam",
//   URSHORT: "Urgent Short Exam",
//   EPSHORT: "Established Patient Short Exam",
// };
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AppointmentService } from "@/services/appointments.service";
import { DoctorsService } from "@/services/doctors.service";
import { DoctorTimeSlotService } from "@/services/doctor-time-slots.service";

// Extend dayjs with UTC and Timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import FooterView from "@/layout/Footer.vue";
import Header from "@/layout/Header.vue";
import { AppointmentTypeService } from "@/services/appointmentType.service";

export default {
  components: {
    FooterView,
    Header,
  },
  name: "DoctorAppointmentsList",
  data() {
    return {
      doctor: {},
      todayAppointments: [],
      upcomingAppointments: [],
      selectedDay: "", // Track selected working day
      loading: true, // Loader state for initial page load
      slotsLoading: false, // Loader state for slots
      selectedAppointmentType: "",
      appointmentTypes: [],            // full list from API
      appointmentTypeMap: {},
      filteredSlotsData: [], // Slots for selected day and appointment type
      availableDays: [], // List of available working days
      doctorId: "",
      hospitalId: "",
    };
  },
  computed: {
    uniqueDays() {
      return this.availableDays;
    },
    filteredSlots() {
      return this.filteredSlotsData;
    },
  },
  watch: {
    selectedDay() {
      this.filteredSlotsData = [];
      if (this.selectedDay && this.selectedAppointmentType) {
        this.fetchSlotsByDay();
      }
    },
    async selectedAppointmentType(newType) {
      if (newType && this.selectedDay) {
        await this.fetchSlotsByDay();
      } else {
        this.filteredSlotsData = [];
      }
    },
  },
  methods: {

    formatTime(time) {
      const local = this.convertToLocalTime(null, time);

      if (!local) return "";

      const [hours, minutes] = local.split(":").map(Number);

      const period = hours >= 12 ? "PM" : "AM";
      const hours12 = hours % 12 || 12;

      return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
    },
    convertToLocalTime(date, time) {
      if (!time) return "";

      let hours, minutes;

      // CASE 1: ISO datetime
      if (time.includes("T")) {
        const d = new Date(time);
        hours = d.getUTCHours();
        minutes = d.getUTCMinutes();
      }
      // CASE 2: HH:mm format
      else {
        [hours, minutes] = time.split(":").map(Number);
      }

      // subtract 6 hours
      hours = hours - 6;

      if (hours < 0) {
        hours = hours + 24;
      }

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    },
    formatDate(dateString) {
      if (!dateString) return "";

      // CASE 1: YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split("-");

        const date = new Date(year, month - 1, day);

        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      // CASE 2: ISO datetime
      if (dateString.includes("T")) {
        const date = new Date(dateString);

        if (isNaN(date)) return "";

        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      // CASE 3: HH:mm (time only)
      if (/^\d{2}:\d{2}/.test(dateString)) {
        return "";
      }

      return "";
    },

    statusClass(status) {
      switch (status) {
        case "open":
          return "status-open";

        case "waiting":
          return "status-waiting";
        case "cancelled":
          return "status-cancelled";
        case "closed":
          return "status-closed";
        case "approved":
          return "status-approved";
        case "started":
          return "status-started";
        case "inprogress":
          return "status-inprogress";
        case "completed":
          return "status-completed";
        default:
          return "status-default"; // For unhandled statuses
      }
    },
    async fetchAppointmentTypes() {
      try {
        const res = await AppointmentTypeService.getAll({ hospitalId: this.hospitalId });
        const data = res?.data?.data;

        if (data) {
          this.appointmentTypes = data.data;

          // Build map: { code: name }
          this.appointmentTypeMap = res.data.reduce((acc, type) => {
            acc[type.code] = type.label;
            return acc;
          }, {});
        }
      } catch (err) {
        console.error("Failed to fetch appointment types", err);
      }
    },
    async fetchAvailableDays(doctorId) {
      try {
        const response = await DoctorTimeSlotService.getSittingDates({
          doctorId: doctorId,
          hospitalId: this.hospitalId,
        });

        console.log("Available Days Response:", response);

        if (response.success) {
          const data = response.data.data;
          // API returns array with enabledDays property
          if (data && data.length > 0 && data[0].enabledDays) {
            this.availableDays = data?.[0].enabledDays;

            // Sort days by weekday order
            const weekOrder = [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ];

            this.availableDays.sort(
              (a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b)
            );

            // Set default selected day to first available day
            if (this.availableDays.length > 0) {
              this.selectedDay = this.availableDays[0];
            }
          }
        }
      } catch (error) {
        console.error("Error fetching available days:", error);
      }
    },
    autoSelectFirstAppointmentTypeAndFetchSlots() {
      // Auto-select first appointment type if available
      if (this.appointmentTypes.length > 0 && !this.selectedAppointmentType) {
        this.$nextTick(() => {
          this.selectedAppointmentType = this.appointmentTypes[0];
        });
      }
    },
    async fetchSlotsByDay() {
      if (!this.selectedDay || !this.selectedAppointmentType) {
        this.filteredSlotsData = [];
        return;
      }

      try {
        this.slotsLoading = true;
        console.log("paylaod", {
          day: this.selectedDay,
          appointmentType: this.selectedAppointmentType,
          doctorId: this.doctorId,
          hospitalId: this.hospitalId,
        });
        const response = await DoctorTimeSlotService.getSlotsV2({
          day: this.selectedDay,
          appointmentType: this.selectedAppointmentType.id,
          doctorId: this.doctorId,
          hospitalId: this.hospitalId,
        });

        console.log("Slots Response:", response);

        if (response.data) {
          const slots = response.data.data;

          if (Array.isArray(slots)) {
            // Filter slots that have this appointment type in appointmentTypeIds
            this.filteredSlotsData = slots.filter((slot) =>
              slot.appointmentTypeIds.includes(this.selectedAppointmentType.id)
            );

            // Sort by start time
            this.filteredSlotsData.sort((a, b) =>
              a.startTime.localeCompare(b.startTime)
            );
          }
        }
      } catch (error) {
        console.error("Error fetching slots by day:", error);
        this.filteredSlotsData = [];
      } finally {
        this.slotsLoading = false;
      }
    },
  },
  async created() {
    const id = this.$route.query.id;
    if (id) {
      try {
        this.doctorId = id;
        this.hospitalId = this.$store.state.user.currentUser.objectId;

        // Fetch appointment types first
        await this.fetchAppointmentTypes();

        const response = await DoctorsService.getById(id, {
          hospitalId: this.hospitalId,
        });

        if (response.success) {
          this.doctor = response.data;
        }

        // Fetch available working days for the doctor
        await this.fetchAvailableDays(id);

        // Auto-select first appointment type after day is selected
        this.autoSelectFirstAppointmentTypeAndFetchSlots();

        const appointment = await AppointmentService.getByDoctorId({
          doctorId: id,
        });

        if (appointment.success) {
          this.todayAppointments = appointment.data.todayAppointments;
          this.upcomingAppointments = appointment.data.upcomingAppointments;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.loading = false; // Hide loader after data is fetched
      }
    } else {
      this.$router.push("/hospital-settings");
    }
  },


};
</script>

<style scoped>
.doc-appoint-list-main {
  justify-content: flex-start !important;
  height: 100%;
}

.doc-appointment-data {
  margin-top: 120px;
  margin-bottom: 60px;
  width: 100%;
}

.work-day {
  cursor: pointer;
  margin: 0px;
  border-radius: 4px;
  background-color: #f0f0f0;
}

.work-day.selected {
  background-color: #007bff;
  color: white;
}

.time-slot {
  padding: 5px 10px;
  margin: 0px;
  background: #f3f3f3;
  border-radius: 4px;
  display: inline-block;
}

/* Loader Styling */
.loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Mini Loader for Slots */
.slots-loader {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  justify-content: center;
}

.mini-spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1.5s linear infinite;
}

.slots-loader p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.time-slots .no-slot-message {
  grid-column: 1 / -1;
  /* ✅ span across all grid columns */
  text-align: left;
  font-size: 16px;
  color: #6c757d;
  padding: 12px 0;
  line-height: 1.4;
  min-height: 200px;
  max-height: 200px;
}
</style>
